import { useEffect } from 'react';
import { useLocation } from '@tanstack/react-router';

interface RouteMetadataProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  imageAlt?: string;
  type?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  tags?: string[];
}

export const RouteMetadata = ({
  title,
  description,
  keywords = 'Airspace, drone monitoring, geofencing, real-time tracking, drone analytics, drone management, UAV, unmanned aircraft, airspace management, flight restrictions, geofencing technology',
  image = '/black-transparent.png',
  imageAlt = 'Uavity - Airspace Monitoring Platform',
  type = 'website',
  publishedTime,
  modifiedTime,
  author = 'Uavity',
  section,
  tags = ['airspace', 'drone monitoring', 'geofencing', 'UAV', 'aviation technology']
}: RouteMetadataProps) => {
  const location = useLocation();
  const canonicalUrl = `https://app.uavity.com${location.pathname}`;
  const fullImageUrl = image.startsWith('http') ? image : `https://app.uavity.com${image}`;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string) => {
      let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updatePropertyMetaTag = (property: string, content: string) => {
      let element = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Update description
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);

    // Update Open Graph tags
    updatePropertyMetaTag('og:title', title);
    updatePropertyMetaTag('og:description', description);
    updatePropertyMetaTag('og:type', type);
    updatePropertyMetaTag('og:url', canonicalUrl);
    updatePropertyMetaTag('og:image', fullImageUrl);
    updatePropertyMetaTag('og:image:alt', imageAlt);
    updatePropertyMetaTag('og:image:width', '1200');
    updatePropertyMetaTag('og:image:height', '630');
    updatePropertyMetaTag('og:site_name', 'Uavity');
    updatePropertyMetaTag('og:locale', 'en_US');

    // Update Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', fullImageUrl);
    updateMetaTag('twitter:image:alt', imageAlt);
    updateMetaTag('twitter:site', '@uavity');
    updateMetaTag('twitter:creator', '@uavity');

    // Additional SEO meta tags
    updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    updateMetaTag('googlebot', 'index, follow');
    updateMetaTag('bingbot', 'index, follow');
    
    // Additional meta tags for better SEO
    updateMetaTag('format-detection', 'telephone=no');
    updateMetaTag('mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');
    updateMetaTag('apple-mobile-web-app-title', 'Uavity');
    updateMetaTag('application-name', 'Uavity');
    updateMetaTag('msapplication-TileColor', '#0f172a');
    updateMetaTag('msapplication-config', '/browserconfig.xml');
    
    // Language and region
    updateMetaTag('language', 'en-US');
    updateMetaTag('geo.region', 'US');
    updateMetaTag('geo.placename', 'United States');
    
    // Content classification
    updateMetaTag('rating', 'general');
    updateMetaTag('distribution', 'global');
    updateMetaTag('revisit-after', '1 days');
    
    if (publishedTime) {
      updateMetaTag('article:published_time', publishedTime);
    }
    if (modifiedTime) {
      updateMetaTag('article:modified_time', modifiedTime);
    }
    if (section) {
      updatePropertyMetaTag('article:section', section);
    }
    if (tags.length > 0) {
      tags.forEach(tag => {
        const existingTags = document.querySelectorAll('meta[property="article:tag"]');
        const tagExists = Array.from(existingTags).some(el => el.getAttribute('content') === tag);
        if (!tagExists) {
          const tagElement = document.createElement('meta');
          tagElement.setAttribute('property', 'article:tag');
          tagElement.setAttribute('content', tag);
          document.head.appendChild(tagElement);
        }
      });
    }

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // Add structured data (JSON-LD)
    const structuredData = {
      "@context": "https://schema.org",
      "@type": type === 'article' ? 'Article' : 'WebSite',
      "name": title,
      "description": description,
      "url": canonicalUrl,
      "image": fullImageUrl,
      "author": {
        "@type": "Organization",
        "name": author
      },
      "publisher": {
        "@type": "Organization",
        "name": "Uavity",
        "logo": {
          "@type": "ImageObject",
          "url": "https://app.uavity.com/black-transparent.png"
        }
      },
      ...(type === 'article' && publishedTime && {
        "datePublished": publishedTime,
        "dateModified": modifiedTime || publishedTime
      }),
      ...(section && { "articleSection": section }),
      ...(tags.length > 0 && { "keywords": tags.join(', ') })
    };

    // Remove existing structured data
    const existingStructuredData = document.querySelector('script[type="application/ld+json"]');
    if (existingStructuredData) {
      existingStructuredData.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }, [title, description, keywords, image, imageAlt, type, publishedTime, modifiedTime, author, section, tags, canonicalUrl, fullImageUrl]);

  return null;
}; 